import { useEffect, useState } from "react";
import { listAgents, deleteAgent } from "../api/agentApi";
import { Link } from "react-router-dom";
import { useUser } from "./UserContext";
import { DescriptionCell } from "./DescriptionCell";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Toolbar,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

export default function NewAgentListPage() {
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const { user, logout } = useUser();

  useEffect(() => {
    listAgents().then(data => setAgents(data.agents || []));
  }, []);

  const handleDelete = async (agentId) => {
    if (!window.confirm(`Delete agent '${agentId}'? This cannot be undone.`)) return;
    setDeleting(agentId);
    try {
      await deleteAgent(agentId);
      setAgents(agents.filter(a => a.agent_id !== agentId));
    } catch (e) {
      setError("Failed to delete agent.");
    }
    setDeleting("");
  };

  // Filter agents based on search query and status
  const filteredAgents = agents.filter(agent => {
    // Text search filter
    const query = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || (
      agent.agent_id?.toLowerCase().includes(query) ||
      agent.name?.toLowerCase().includes(query) ||
      agent.display_name?.toLowerCase().includes(query) ||
      agent.description?.toLowerCase().includes(query) ||
      agent.owner?.toLowerCase().includes(query) ||
      agent.status?.toLowerCase().includes(query)
    );

    // Status filter
    const matchesStatus = selectedStatus === "all" || agent.status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ pt:1, px: 3 }}>
      <Toolbar 
      disableGutters 
      sx={{ 
        display: "flex", 
        alignItems: "center",
        justifyContent: "space-between",
      }}
      >
        <Typography variant="h4">Agents</Typography>
        {user?.role === 'admin' &&
          <Button
            component={Link}
            to="/agent/conversational"
            variant="contained"
            color="primary"
            
            sx={{ mt: 1, mb: 2, fontSize: "1rem", px: 3, py: 1, borderRadius: "99px", textTransform: 'none'}}
          >
          Create New Agent
          </Button>
      }
      </Toolbar>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 2, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search agents by ID, name, description, owner, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Status Filter */}
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={selectedStatus}
                label="Status"
                onChange={(e) => setSelectedStatus(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterListIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="building">Building</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      
      
      {/* Results Count */}
      {(searchQuery || selectedStatus !== "all") && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Found {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
            {selectedStatus !== "all" && ` with status "${selectedStatus}"`}
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Agent Name</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem", width: 480 }}>Agent Description</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Current Version</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery ? 'No agents found matching your search.' : 'No agents available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAgents.map(agent => (
              <TableRow key={agent.agent_id} hover sx={{ fontSize: "0.95rem" }}>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>
                  <Link to={`/agent/${agent.agent_id}`} style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                    {agent.display_name ? agent.display_name : agent.agent_id}
                  </Link>
                </TableCell>
                <DescriptionCell description={agent.description} />
                <TableCell sx={{ py: 1 }}>
                  <Chip
                    label={agent.status || "Unverified"}
                    sx={{
                      bgcolor: agent.status === "ready"
                        ? "#43a047"
                        : agent.status === "building"
                          ? "#ffb300"
                          : agent.status === "created"
                            ? "#1976d2"
                            : "#e0e0e0",
                      color: agent.status === "ready" ? "#fff" : "#333",
                      fontWeight: 500,
                      borderRadius: "9999px", // fully rounded
                      height: 24,
                      px: 1.5,
                    }}
                />
                </TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1, pl: 3 }}>{agent.version || "N/A"}</TableCell>
                <TableCell sx={{ fontSize: "0.95rem", py: 1 }}>{agent.owner}</TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Button
                    onClick={() => handleDelete(agent.agent_id)}
                    disabled={deleting === agent.agent_id}
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: "0.85rem", px: 1.5, py: 0.5, borderRadius: "99px", textTransform: 'none' }}
                  >
                    {deleting === agent.agent_id ? "Deleting..." : "Delete"}
                  </Button>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </TableContainer>
      
    </Box>
   
  );
}